import { useCallback, useEffect, useMemo, useState } from 'react'
import { useWatch } from 'react-hook-form'

import useFlatAllowanceCalculations from 'components/hooks/quotes/useFlatAllowanceCalculations'
import { getDefaultAllowance } from 'components/quoting/quotes/flat_allowances/FlatAllowanceExplorer'
import {
  getBenchmarkAvailabilityFlags,
  getCoverageLevelPills,
  getCurrentQuoteResults,
  getDefaultFilterValues,
  getEligibleEmployees,
  getIneligibleEmployees,
  getMonthlyAllowanceForEmployee as getMonthlyAllowanceForEmployeeFromCalculations,
  getRecommendedAllowanceStrategy,
  getRecommendedDependentCoverageLevel,
  getWillEnrollEmployees,
  isFlatRecommended,
} from 'pages/employer_onboarding/quote/quoteCalculations'
import {
  AllowanceStrategies,
  type AllowanceStrategy,
  type CoverageLevel,
  type FiltersValues,
} from 'pages/employer_onboarding/quote/types'
import {
  flatAllowanceIsAffordable,
  planIsAffordable,
} from 'utils/quote_calculations'
import {
  type PutQuotingProspectsIdReasonsForChangingInsuranceItem as ReasonsForChangingInsurance,
  type QuotingPlanAvailability,
  type QuotingProspect,
  type QuotingQuote,
  type QuotingQuoteAllowance,
  type QuotingQuoteAllowanceBenchmark,
  type QuotingQuoteResult,
  type QuotingRecommendedQuoteResults,
} from 'utils/web_api.schemas'

// Create a testable function to determine coverage level from benchmark data
export function determineCoverageLevelFromBenchmark (
  quoteResultsByCoverageLevel: QuotingRecommendedQuoteResults | undefined,
  benchmarkMetalTier: QuotingQuoteAllowanceBenchmark,
  benchmarkCoveragePercentile: number
): CoverageLevel {
  if (!quoteResultsByCoverageLevel) return 'best_fit'

  // Check each coverage level to see if it matches the benchmark criteria
  const coverageLevels: CoverageLevel[] = [
    'lowest_cost',
    'best_fit',
    'better_benefits',
  ]

  for (const level of coverageLevels) {
    const levelData = quoteResultsByCoverageLevel[level]
    if (levelData?.benchmark) {
      const matchesMetalTier =
        'on_thatch_' + levelData.benchmark.metal_tier === benchmarkMetalTier
      const matchesPercentile =
        levelData.benchmark.coverage_percentile ===
        benchmarkCoveragePercentile?.toString()

      if (matchesMetalTier && matchesPercentile) {
        return level
      }
    }
  }

  // Fallback to best_fit if no match found
  return 'best_fit'
}

export function useQuoteExplorerState (
  quote: QuotingQuote | null,
  prospect: QuotingProspect | null,
  quoteResultsByCoverageLevel: QuotingRecommendedQuoteResults | undefined,
  flatAllowanceEmployees: QuotingQuoteResult[] | undefined,
  planAvailability: QuotingPlanAvailability[] | undefined,
  isLoading: boolean
) {
  const allowances = useWatch({ name: 'allowances' }) ?? [
    getDefaultAllowance(),
  ]

  const recommendedDependentCoverageLevel = useMemo(() => {
    return getRecommendedDependentCoverageLevel(
      prospect?.reasons_for_changing_insurance as ReasonsForChangingInsurance[]
    )
  }, [prospect?.reasons_for_changing_insurance])

  const defaultFilterValues = useMemo(() => {
    return getDefaultFilterValues(recommendedDependentCoverageLevel)
  }, [recommendedDependentCoverageLevel])

  const [filterValues, setFilterValues] =
    useState<FiltersValues>(defaultFilterValues)
  const [selectedCoverageLevel, setSelectedCoverageLevel] =
    useState<CoverageLevel>('best_fit')
  const [showIneligibleEmployees, setShowIneligibleEmployees] = useState(false)

  const currentQuoteResults = useMemo(() => {
    return getCurrentQuoteResults(
      quoteResultsByCoverageLevel,
      selectedCoverageLevel
    )
  }, [quoteResultsByCoverageLevel, selectedCoverageLevel])

  const eligibleEmployees = useMemo(() => {
    return getEligibleEmployees(currentQuoteResults)
  }, [currentQuoteResults])

  const ineligibleEmployees = useMemo(() => {
    return getIneligibleEmployees(currentQuoteResults)
  }, [currentQuoteResults])

  const isFlatRecommendedValue = useMemo(() => {
    return isFlatRecommended(eligibleEmployees, isLoading)
  }, [eligibleEmployees, isLoading])

  const recommendedAllowanceStrategy = useMemo(() => {
    return getRecommendedAllowanceStrategy(isFlatRecommendedValue)
  }, [isFlatRecommendedValue])

  const [allowanceStrategy, setAllowanceStrategy] = useState<AllowanceStrategy>(
    recommendedAllowanceStrategy
  )

  const [willEnrollEmployeeIds, setWillEnrollEmployeeIds] = useState<
    Set<string>
  >(() => new Set(eligibleEmployees.map((emp: QuotingQuoteResult) => emp.id)))

  const willEnrollEmployees = useMemo(() => {
    return getWillEnrollEmployees(
      allowanceStrategy,
      currentQuoteResults,
      flatAllowanceEmployees,
      willEnrollEmployeeIds
    )
  }, [
    allowanceStrategy,
    currentQuoteResults,
    flatAllowanceEmployees,
    willEnrollEmployeeIds,
  ])

  const flatAllowanceCosts = useFlatAllowanceCalculations(
    willEnrollEmployees,
    allowances
  )

  const onWillEnrollEmployeeIdsChange = useCallback(
    (employeeIds: Set<string>) => {
      setWillEnrollEmployeeIds(employeeIds)
    },
    []
  )

  const resetFilterValues = useCallback(() => {
    setFilterValues(defaultFilterValues)
    setSelectedCoverageLevel('best_fit')
  }, [defaultFilterValues])

  const coverageLevelPills = useMemo(() => {
    return getCoverageLevelPills(
      selectedCoverageLevel,
      prospect?.reasons_for_changing_insurance as ReasonsForChangingInsurance[],
      prospect?.offers_health_insurance_today,
      eligibleEmployees
    )
  }, [selectedCoverageLevel, prospect, eligibleEmployees])

  const { hasLowestCostBenchmark, hasBetterBenefitsBenchmark } = useMemo(() => {
    return getBenchmarkAvailabilityFlags(quoteResultsByCoverageLevel)
  }, [quoteResultsByCoverageLevel])

  const getEmployeeById = useCallback(
    (employeeId: string) => {
      return currentQuoteResults.find(
        (employee: QuotingQuoteResult) => employee.id === employeeId
      )
    },
    [currentQuoteResults]
  )

  const getPlanAvailabilityForEmployee = useCallback(
    (employee: QuotingQuoteResult) => {
      if (!planAvailability) return undefined
      return planAvailability.find(
        (availability: QuotingPlanAvailability) =>
          availability.rating_area_code === employee.rating_area_code
      )
    },
    [planAvailability]
  )

  const getTotalPlansForEmployee = useCallback(
    (employee: QuotingQuoteResult) => {
      const availability = getPlanAvailabilityForEmployee(employee)
      if (!availability?.plan_counts) return 0
      return Object.values(availability.plan_counts).reduce(
        (sum, count) => sum + count,
        0
      )
    },
    [getPlanAvailabilityForEmployee]
  )

  const getMonthlyAllowanceForEmployee = useCallback(
    (employeeId: string) => {
      return getMonthlyAllowanceForEmployeeFromCalculations(
        employeeId,
        allowanceStrategy,
        flatAllowanceCosts,
        filterValues,
        currentQuoteResults
      )
    },
    [allowanceStrategy, flatAllowanceCosts, filterValues, currentQuoteResults]
  )

  const isAllowanceAffordable = useCallback(
    (employeeId: string) => {
      const employee = currentQuoteResults.find(
        (employee: QuotingQuoteResult) => employee.id === employeeId
      )
      if (!employee) return false

      if (allowanceStrategy === AllowanceStrategies.Smart) {
        return planIsAffordable(employee, {
          employerEmployeeContribution:
            filterValues.employerEmployeeContribution,
        })
      } else {
        const monthlyAllowance = getMonthlyAllowanceForEmployee(employeeId)
        return flatAllowanceIsAffordable(employee, monthlyAllowance)
      }
    },
    [
      allowanceStrategy,
      currentQuoteResults,
      filterValues,
      getMonthlyAllowanceForEmployee,
    ]
  )

  const getMinAffordableAllowanceForEmployee = useCallback(
    (employeeId: string) => {
      const employee = currentQuoteResults.find(
        (employee: QuotingQuoteResult) => employee.id === employeeId
      )
      if (!employee) return 0

      return employee.min_employer_contribution
    },
    [currentQuoteResults]
  )

  const getStateCodeFromRatingArea = useCallback(
    (ratingAreaCode: string | null | undefined): string | null => {
      if (!ratingAreaCode) return null
      return ratingAreaCode.substring(0, 2)
    },
    []
  )

  const isEmployeeStateUsingProjectedData = useCallback(
    (employee: QuotingQuoteResult): boolean => {
      if (!quote?.preliminary) return false
      if (!quote?.projected_data_state_codes) return false

      const stateCode = getStateCodeFromRatingArea(employee.rating_area_code)
      if (!stateCode) return false

      return quote.projected_data_state_codes.includes(stateCode)
    },
    [
      quote?.preliminary,
      quote?.projected_data_state_codes,
      getStateCodeFromRatingArea,
    ]
  )

  useEffect(() => {
    if (!isLoading && eligibleEmployees.length > 0) {
      const newIds = new Set(
        eligibleEmployees.map((emp: QuotingQuoteResult) => emp.id)
      )
      setWillEnrollEmployeeIds(newIds)
    }
  }, [isLoading, eligibleEmployees])

  // Set recommended allowance strategy on initial load when no saved allowances exist
  useEffect(() => {
    if (!isLoading && (!quote?.allowances || quote.allowances.length === 0)) {
      setAllowanceStrategy(recommendedAllowanceStrategy)
    }
  }, [isLoading, recommendedAllowanceStrategy, quote?.allowances])

  useEffect(() => {
    if (quote?.allowances && quote.allowances.length > 0) {
      const savedAllowances = quote.allowances

      const dynamicAllowance = savedAllowances.find(
        (allowance: QuotingQuoteAllowance) =>
          allowance.coverage_percentile != null
      )
      if (dynamicAllowance) {
        setAllowanceStrategy(AllowanceStrategies.Smart)
        setFilterValues({
          coverageLevel: 'best_fit',
          employerEmployeeContribution:
            (dynamicAllowance.employee_contribution_percentage ?? 100) / 100,
          employerSpouseContribution:
            (dynamicAllowance.spouse_contribution_percentage ?? 50) / 100,
          employerChildContribution:
            (dynamicAllowance.dependent_contribution_percentage ?? 50) / 100,
        })

        if (dynamicAllowance.benchmark && dynamicAllowance.coverage_percentile) {
          const coverageLevel = determineCoverageLevelFromBenchmark(
            quoteResultsByCoverageLevel,
            dynamicAllowance.benchmark,
            dynamicAllowance.coverage_percentile
          )
          setSelectedCoverageLevel(coverageLevel)
        }
        return
      }
      setAllowanceStrategy(AllowanceStrategies.Flat)
    }
  }, [quote?.allowances, quoteResultsByCoverageLevel])

  return {
    filterValues,
    setFilterValues,
    selectedCoverageLevel,
    setSelectedCoverageLevel,
    showIneligibleEmployees,
    setShowIneligibleEmployees,
    allowanceStrategy,
    setAllowanceStrategy,
    willEnrollEmployeeIds,
    setWillEnrollEmployeeIds,
    onWillEnrollEmployeeIdsChange,
    willEnrollEmployees,
    eligibleEmployees,
    ineligibleEmployees,
    currentQuoteResults,
    isFlatRecommended: isFlatRecommendedValue,
    coverageLevelPills,
    hasLowestCostBenchmark,
    hasBetterBenefitsBenchmark,
    getEmployeeById,
    getPlanAvailabilityForEmployee,
    getTotalPlansForEmployee,
    getMonthlyAllowanceForEmployee,
    resetFilterValues,
    allowances,
    isAllowanceAffordable,
    getMinAffordableAllowanceForEmployee,
    getStateCodeFromRatingArea,
    isEmployeeStateUsingProjectedData,
  }
}
